/**
 * P164: presentation-only helpers for the MOU import screens.
 *
 * Deliberately holds NO copy of the eight steps. Their numbers, titles,
 * summaries and document checklists all arrive from the server on the case
 * itself - a second list here would be a second definition of the process, and
 * the two would drift the first time a step's paperwork changed. What lives
 * here is only what the server has no opinion about: how a status is worded in
 * Thai, which badge tone it gets, and how a byte count reads to a person.
 */

export const MOU_CASE_STATUS_OPEN = "Open";
export const MOU_CASE_STATUS_COMPLETED = "Completed";
export const MOU_CASE_STATUS_CANCELLED = "Cancelled";

export const MOU_STEP_STATUS_PENDING = "Pending";
export const MOU_STEP_STATUS_IN_PROGRESS = "InProgress";
export const MOU_STEP_STATUS_DONE = "Done";

/** The three step states, in the order an operator moves through them. */
export const MOU_STEP_STATUS_OPTIONS = [
  { value: MOU_STEP_STATUS_PENDING, label: "ยังไม่เริ่ม" },
  { value: MOU_STEP_STATUS_IN_PROGRESS, label: "กำลังดำเนินการ" },
  { value: MOU_STEP_STATUS_DONE, label: "เสร็จแล้ว" },
];

export const MOU_CASE_STATUS_OPTIONS = [
  { value: MOU_CASE_STATUS_OPEN, label: "กำลังดำเนินการ" },
  { value: MOU_CASE_STATUS_COMPLETED, label: "เสร็จสิ้น" },
  { value: MOU_CASE_STATUS_CANCELLED, label: "ยกเลิก" },
];

/**
 * The status an operator reads, never the enum the API stores. The raw value is
 * a fallback for a status a future packet adds before this list catches up -
 * showing the code is better than showing nothing, but neither should be the
 * normal case.
 */
export function stepStatusLabel(status) {
  return MOU_STEP_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status ?? "-";
}

export function caseStatusLabel(status) {
  return MOU_CASE_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status ?? "-";
}

/** Maps a step status onto the shared .wx-badge tones. */
export function stepStatusVariant(status) {
  if (status === MOU_STEP_STATUS_DONE) {
    return "success";
  }
  if (status === MOU_STEP_STATUS_IN_PROGRESS) {
    return "info";
  }
  return "neutral";
}

export function caseStatusVariant(status) {
  if (status === MOU_CASE_STATUS_COMPLETED) {
    return "success";
  }
  if (status === MOU_CASE_STATUS_CANCELLED) {
    return "danger";
  }
  return "info";
}

/** Human file size. Bytes are what the server records; nobody reads 2097152. */
export function formatSizeBytes(sizeBytes) {
  if (sizeBytes == null) {
    return null;
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} KB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A plain yyyy-MM-dd calendar date, rendered in Thai with a Gregorian year.
 *
 * Split on the hyphens rather than handed to `new Date(...)`: a bare
 * "2026-08-03" is parsed as UTC midnight and then rendered in the browser's own
 * zone, which is exactly how a filing dated the 3rd starts displaying as the
 * 2nd for anyone west of Greenwich.
 */
export function formatCalendarDate(value) {
  if (!value) {
    return null;
  }
  const parts = String(value).split("-");
  if (parts.length !== 3) {
    return null;
  }
  const [year, month, day] = parts.map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  const local = new Date(year, month - 1, day);
  if (Number.isNaN(local.getTime())) {
    return null;
  }
  return local.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

/** A stored instant (createdAtUtc/uploadedAtUtc), which genuinely is a moment in time. */
export function formatInstant(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Operator-facing wording for a failed call.
 *
 * A 400 is the server's own validated refusal and its message already names
 * what to do instead ("รองรับเฉพาะไฟล์ PDF, JPG, PNG หรือ WEBP"), so it is passed
 * through rather than replaced with something vaguer.
 */
export function describeMouError(error, fallback = "ไม่สามารถทำรายการได้ กรุณาลองใหม่อีกครั้ง") {
  if (error?.status === 400 && error?.body?.message) {
    return error.body.message;
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์จัดการเรื่องยื่น MOU ของบริษัทนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบข้อมูลที่ต้องการ อาจถูกลบไปแล้ว";
  }
  if (error?.status === 413) {
    return "ไฟล์ใหญ่เกินไป กรุณาใช้ไฟล์ที่มีขนาดไม่เกิน 20 MB";
  }
  return fallback;
}

/**
 * How far through the process a case is, as a percentage, for the header meter.
 * Guards a zero step count so a malformed response renders an empty bar instead
 * of NaN%.
 */
export function progressPercent(completedStepCount, stepCount) {
  if (!stepCount) {
    return 0;
  }
  return Math.round((Math.max(0, Math.min(completedStepCount ?? 0, stepCount)) / stepCount) * 100);
}

/**
 * The file types the MOU storage profile accepts, as an `accept` attribute.
 * This only filters the file dialog - the real check is the server's own
 * magic-byte test, which this can never replace.
 */
export const MOU_DOCUMENT_ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";

export const MOU_DOCUMENT_HINT = "รองรับไฟล์ PDF, JPG, PNG หรือ WEBP ขนาดไม่เกิน 20 MB";
